import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { Typography, SearchInput } from 'app/components/ui';
import { TagListItem } from './TagListItem';
import { ChainGroup } from './ChainGroup';
import { groupTagsIntoChains } from './groupChains';
import { sortTagsByDocumentOrder } from './documentOrder';
import { TagWithMetadata, useTagsByDocument } from 'entities/tag';
import { Spinner } from 'app/components';
import { FilterDropdown, useFilteredTags, FilterResults, FilterGroup } from '../TagsFilter';

import './AllTags.scss';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

interface AllTagsProps {
  selectedTag: TagWithMetadata | null;
  onSelectTag: (tag: TagWithMetadata | null) => void;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onExtensionClick?: (tagId: string) => void;
  onDeleteClick?: (tag: TagWithMetadata) => void;
  filterTagIds?: string[] | null;
}

export const AllTags = ({ selectedTag, onSelectTag, onTagDetailsClick, onExtensionClick, onDeleteClick, filterTagIds }: AllTagsProps) => {
  const { t } = useTranslation(['all', 'tag']);

  const section = useEditorLiveSection();
  const { data: pDocument } = useEditorDocument();
  const { data: tagsResponse, isLoading } = useTagsByDocument(pDocument?.superId, {
    include: ['concept', 'metadata', 'unit', 'context'],
  });
  const tagsData = useMemo(() => tagsResponse?.tags ?? [], [tagsResponse]);

  const [showCurrentSectionOnly, setShowCurrentSectionOnly] = useState(false);
  const currentSectionId = section?.superId || null;

  const sectionFilter = useCallback(
    (tag: TagWithMetadata) => !showCurrentSectionOnly || tag.metadata?.sectionSuperId === currentSectionId,
    [showCurrentSectionOnly, currentSectionId]
  );

  const filterGroups: FilterGroup[] = [
    {
      label: t('tag:highlighted.option.status'),
      value: 'status',
      icon: <i className="fa-solid fa-spinner" />,
      options: [
        { label: t('tag:status.approved'), value: 'approved' },
        { label: t('tag:status.draft'), value: 'draft' },
        { label: t('tag:status.need_attention'), value: 'need_attention' },
        { label: t('tag:status.verified'), value: 'verified' },
      ],
    },
    {
      label: t('tag:validation.label'),
      value: 'validated',
      icon: <i className="fa-solid fa-flag" />,
      options: [
        { label: t('tag:status.invalid'), value: 'invalid' },
        { label: t('tag:status.unvalidated'), value: 'unvalidated' },
        { label: t('tag:status.valid'), value: 'valid' },
      ],
    },
    {
      label: t('tag:type'),
      value: 'type',
      icon: <i className="fa-solid fa-tags" />,
      options: [
        { label: t('tag:type.numeric'), value: 'nonFraction' },
        { label: t('tag:type.textBlock'), value: 'nonNumeric' },
        { label: t('tag:type.fraction'), value: 'fraction' },
      ],
    },
  ];

  const { filters, filteredTags, onAddFilter, onRemoveFilter, searchInput, setSearchInput } = useFilteredTags(
    tagsData,
    filterGroups,
    filterTagIds,
    null,
    sectionFilter
  );

  const orderedTags = useMemo(() => sortTagsByDocumentOrder(filteredTags), [filteredTags]);

  const listEntries = useMemo(() => groupTagsIntoChains(orderedTags, tagsData), [orderedTags, tagsData]);

  return (
    <section className="all-tags">
      <div className="all-tags__header">
        <div className="all-tags__header--search-row">
          <SearchInput searchInput={searchInput} setSearchInput={setSearchInput} />
          <FilterDropdown iconOnly onSelect={onAddFilter} selected={filters} groups={filterGroups} />
        </div>

        <div className="all-tags__header--filter-row">
          <span className="all-tags__header--checkbox-label">
            <input className="fs-3" type="checkbox" checked={showCurrentSectionOnly} onChange={() => setShowCurrentSectionOnly((prev) => !prev)} />
            <Typography variant="body-5" fontWeight="extra-light">
              {t('all:tags.show.current.section')}
            </Typography>
          </span>

          <Typography variant="body-3" fontWeight="extra-light">
            {`${filteredTags.length} ${t('all:tags.of')} ${tagsData?.length ?? 0} ${t('all:tags.tags')}`}
          </Typography>
        </div>

        <FilterResults filters={filters} onRemove={onRemoveFilter} />
      </div>

      <div className="all-tags__content">
        {isLoading ? (
          <>
            <Typography>{t('all:tags.loading')}</Typography>
            <Spinner />
          </>
        ) : filteredTags.length > 0 ? (
          <ul className="all-tags__list">
            {listEntries.map((entry) =>
              entry.kind === 'chain' ? (
                <ChainGroup
                  key={entry.rootId}
                  tags={entry.tags}
                  selectedTag={selectedTag}
                  onSelectTag={onSelectTag}
                  onTagDetailsClick={onTagDetailsClick}
                  onExtensionClick={onExtensionClick}
                  onDeleteClick={onDeleteClick}
                />
              ) : (
                <TagListItem
                  key={entry.tag.id}
                  tag={entry.tag}
                  selectedTag={selectedTag}
                  onSelectTag={onSelectTag}
                  onTagDetailsClick={onTagDetailsClick}
                  onExtensionClick={onExtensionClick}
                  onDeleteClick={onDeleteClick}
                />
              )
            )}
          </ul>
        ) : (
          <Typography>{t('all:tags.empty.list')}</Typography>
        )}
      </div>
    </section>
  );
};
